import { useState, useMemo } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PropertyCard from "@/components/PropertyCard";
import { useApp } from "@/context/AppContext";
import { properties } from "@/data/site";

const CATEGORIES = [
  { label: "Tutte", test: () => true },
  { label: "Ville", test: (p) => p.category === "villa" },
  { label: "Appartamenti", test: (p) => p.category === "apartment" },
  { label: "Camere e B&B", test: (p) => p.category === "bnb" || p.category === "room" },
  { label: "Abruzzo", test: (p) => p.region === "Abruzzo" },
  { label: "Roma", test: (p) => p.city === "Roma" },
];

const ADV = [
  { key: "pool", label: "Piscina" },
  { key: "historic", label: "Centro storico" },
  { key: "kitchen", label: "Cucina" },
  { key: "wifi", label: "Wi-Fi" },
  { key: "parking", label: "Parcheggio" },
  { key: "petFriendly", label: "Pet-friendly" },
];

export default function Properties() {
  const { propFilter, setPropFilter } = useApp();
  const [showAdv, setShowAdv] = useState(false);
  const [adv, setAdv] = useState([]);
  const [minGuests, setMinGuests] = useState(1);
  const [maxPrice, setMaxPrice] = useState(300);

  const activeCat = CATEGORIES.find((c) => c.label === propFilter) || CATEGORIES[0];

  const list = useMemo(() => {
    // filtro dal componente Destinazioni (per città)
    const cityMatch = CATEGORIES.every((c) => c.label !== propFilter);
    return properties.filter((p) => p.active).filter((p) => {
      if (cityMatch && propFilter && propFilter !== "Tutte") {
        if (p.city !== propFilter) return false;
      } else if (!activeCat.test(p)) return false;
      if (adv.length && !adv.every((a) => p.amenities.includes(a))) return false;
      if (p.maxGuests < minGuests) return false;
      if (p.startingPrice && p.startingPrice > maxPrice) return false;
      return true;
    });
  }, [propFilter, adv, minGuests, maxPrice, activeCat]);

  const toggleAdv = (k) => setAdv((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  return (
    <section id="strutture" className="py-24 md:py-32 bg-brand-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="text-xs tracking-[0.35em] uppercase text-brand-gold mb-4">La collezione</p>
          <h2 className="font-heading font-light text-4xl md:text-5xl text-brand-black">Le nostre strutture</h2>
          <p className="mt-4 text-brand-brown text-lg">Scegli il soggiorno più adatto al tuo viaggio.</p>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.label}
              data-testid={`filter-${c.label}`}
              onClick={() => setPropFilter(c.label)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${propFilter === c.label ? "bg-brand-olive text-white border-brand-olive" : "bg-transparent text-brand-brown border-brand-ivory hover:border-brand-sage"}`}
            >
              {c.label}
            </button>
          ))}
          <button data-testid="toggle-advanced" onClick={() => setShowAdv((v) => !v)} className="ml-auto inline-flex items-center gap-2 text-sm text-brand-olive font-medium">
            <SlidersHorizontal className="w-4 h-4" /> Filtri avanzati
          </button>
        </div>

        {showAdv && (
          <div data-testid="advanced-filters" className="mt-5 p-6 bg-white border border-brand-ivory rounded-2xl grid gap-6 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-brand-black mb-3">Servizi</p>
              <div className="flex flex-wrap gap-2">
                {ADV.map((a) => (
                  <button key={a.key} onClick={() => toggleAdv(a.key)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${adv.includes(a.key) ? "bg-brand-sage text-white border-brand-sage" : "border-brand-ivory text-brand-brown"}`}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-black">Ospiti minimi: {minGuests}</label>
              <input type="range" min="1" max="8" value={minGuests} onChange={(e) => setMinGuests(Number(e.target.value))} className="w-full mt-3 accent-brand-olive" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-black">Prezzo max: €{maxPrice}</label>
              <input type="range" min="50" max="300" step="10" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full mt-3 accent-brand-olive" />
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {list.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
        {list.length === 0 && <p className="mt-10 text-center text-brand-brown">Nessuna struttura corrisponde ai filtri selezionati.</p>}
      </div>
    </section>
  );
}
